import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import {
  Sparkles, ChevronLeft, ChevronRight, Filter, CheckCircle, XCircle,
  ArrowRight, Loader2, Brain, BarChart3, RefreshCw, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { automatizacionAPI } from '../api';

const CAMPO_LABELS = {
  titulo: 'Titulo',
  expediente: 'Expediente',
  tipo_tramite: 'Tipo tramite',
  tiene_plazo: 'Plazo',
};

const CAMPO_COLORS = {
  titulo: 'bg-blue-100 text-blue-700',
  expediente: 'bg-purple-100 text-purple-700',
  tipo_tramite: 'bg-amber-100 text-amber-700',
  tiene_plazo: 'bg-red-100 text-red-700',
};

function formatDate(d) {
  if (!d) return '-';
  const date = new Date(d);
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatWeek(fecha) {
  if (!fecha) return '';
  const d = new Date(fecha);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

export default function IAFeedbackPanel() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [evolucion, setEvolucion] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [soloCorrecciones, setSoloCorrecciones] = useState(false);
  const [recalculando, setRecalculando] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await automatizacionAPI.getIAFeedbackStats();
      setStats(res.data);
    } catch (err) {
      console.error('Error cargando stats feedback:', err);
    }
  }, []);

  const loadEvolucion = useCallback(async () => {
    try {
      const res = await automatizacionAPI.getIAFeedbackEvolucion();
      setEvolucion(res.data.semanas || []);
    } catch (err) {
      console.error('Error cargando evolucion:', err);
    }
  }, []);

  const loadHistorial = useCallback(async () => {
    setLoading(true);
    try {
      const res = await automatizacionAPI.getIAFeedbackHistorial({
        page,
        page_size: 10,
        solo_correcciones: soloCorrecciones,
      });
      setHistorial(res.data.items || []);
      setTotalPages(res.data.total_pages || 0);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error cargando historial feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [page, soloCorrecciones]);

  useEffect(() => { loadStats(); loadEvolucion(); }, [loadStats, loadEvolucion]);
  useEffect(() => { loadHistorial(); }, [loadHistorial]);

  const handleRecalcular = async () => {
    setRecalculando(true);
    try {
      const res = await automatizacionAPI.recalcularIAFeedback();
      if (res.data.success) {
        loadStats();
        loadEvolucion();
        loadHistorial();
      }
    } catch (err) {
      console.error('Error recalculando:', err);
    } finally {
      setRecalculando(false);
    }
  };

  const precisionColor = (stats?.precision_ia || 0) >= 80 ? 'text-green-600' : (stats?.precision_ia || 0) >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-4" data-testid="ia-feedback-panel">
      {/* Stats Summary */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Brain className="h-4 w-4 text-teal-600" />
              Aprendizaje de la IA - Resumen
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleRecalcular}
              disabled={recalculando}
              data-testid="recalcular-feedback-btn"
            >
              {recalculando ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Recalcular
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-800">{stats.total_confirmaciones}</p>
                <p className="text-xs text-gray-500 mt-1">Total confirmaciones</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stats.sin_correccion}</p>
                <p className="text-xs text-gray-500 mt-1">IA acerto</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{stats.con_correccion}</p>
                <p className="text-xs text-gray-500 mt-1">Con correcciones</p>
              </div>
              <div className="text-center p-3 bg-teal-50 rounded-lg">
                <p className={`text-2xl font-bold ${precisionColor}`}>{stats.precision_ia}%</p>
                <p className="text-xs text-gray-500 mt-1">Precision global</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )}

          {/* Desglose por campo */}
          {stats?.campos_corregidos && stats.con_correccion > 0 && (
            <div className="mt-4 pt-3 border-t">
              <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                <BarChart3 className="h-3.5 w-3.5" /> Errores por campo
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.campos_corregidos).map(([campo, count]) => (
                  count > 0 && (
                    <div key={campo} className="flex items-center gap-1.5">
                      <Badge className={CAMPO_COLORS[campo] || 'bg-gray-100 text-gray-700'}>
                        {CAMPO_LABELS[campo] || campo}
                      </Badge>
                      <span className="text-sm font-semibold text-gray-700">{count}</span>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evolution Chart */}
      {evolucion.length > 1 && (
        <Card data-testid="chart-evolucion-ia">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-teal-600" />
              Evolucion de la Precision IA (semanal)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={evolucion}>
                <defs>
                  <linearGradient id="gradPrecision" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="fecha" tickFormatter={formatWeek} tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'precision') return [`${value}%`, 'Precision'];
                    return [value, name];
                  }}
                  labelFormatter={(label) => `Semana del ${formatWeek(label)}`}
                />
                <Area type="monotone" dataKey="precision" stroke="#14b8a6" strokeWidth={2} fill="url(#gradPrecision)" name="precision" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-2 text-xs text-gray-500">
              {evolucion.length > 0 && (
                <>
                  <span>Total emails analizados: {evolucion.reduce((s, e) => s + e.total, 0)}</span>
                  <span>Semanas: {evolucion.length}</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historial table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-600" />
              Historial de correcciones ({total})
            </CardTitle>
            <Button
              variant={soloCorrecciones ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => { setSoloCorrecciones(!soloCorrecciones); setPage(1); }}
              data-testid="filter-solo-correcciones"
            >
              <Filter className="h-3 w-3 mr-1" />
              {soloCorrecciones ? 'Solo correcciones' : 'Todas'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : historial.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-6">No hay registros de feedback todavia. Se generaran automaticamente al confirmar emails.</p>
          ) : (
            <div className="space-y-2">
              {historial.map((fb) => (
                <div
                  key={fb.id}
                  className={`p-3 rounded-lg border text-sm ${fb.hubo_correccion ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}
                  data-testid={`feedback-item-${fb.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {fb.hubo_correccion ? (
                          <XCircle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                        ) : (
                          <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />
                        )}
                        <span className="font-medium text-gray-800 truncate">{fb.asunto_email || 'Sin asunto'}</span>
                        {fb.campos_corregidos?.map((c) => (
                          <Badge key={c} className={`text-[10px] px-1.5 py-0 ${CAMPO_COLORS[c] || 'bg-gray-100 text-gray-600'}`}>
                            {CAMPO_LABELS[c] || c}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {fb.remitente} &middot; {fb.usuario_nombre || 'Sistema'} &middot; {formatDate(fb.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Detalles de correcciones */}
                  {fb.hubo_correccion && fb.campos_corregidos?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-amber-200 space-y-1">
                      {fb.campos_corregidos.includes('titulo') && (
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-gray-500 w-16 shrink-0">Titulo:</span>
                          <span className="text-red-600 line-through truncate max-w-[40%]">{fb.ia_titulo}</span>
                          <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                          <span className="text-green-700 font-medium truncate max-w-[40%]">{fb.user_titulo}</span>
                        </div>
                      )}
                      {fb.campos_corregidos.includes('expediente') && (
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-gray-500 w-16 shrink-0">Exp:</span>
                          <span className="text-red-600">{fb.ia_expediente_numero || '?'}</span>
                          <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                          <span className="text-green-700 font-medium">{fb.user_expediente_numero}</span>
                        </div>
                      )}
                      {fb.campos_corregidos.includes('tipo_tramite') && (
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-gray-500 w-16 shrink-0">Tipo:</span>
                          <span className="text-red-600 capitalize">{fb.ia_tipo_tramite}</span>
                          <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                          <span className="text-green-700 font-medium capitalize">{fb.user_tipo_tramite}</span>
                        </div>
                      )}
                      {fb.campos_corregidos.includes('tiene_plazo') && (
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-gray-500 w-16 shrink-0">Plazo:</span>
                          <span className="text-red-600">{fb.ia_tiene_plazo ? 'Si' : 'No'}</span>
                          <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                          <span className="text-green-700 font-medium">{fb.user_tiene_plazo ? 'Si' : 'No'}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">Pagina {page} de {totalPages}</span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  data-testid="feedback-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="feedback-next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
